import type React from 'react';

/**
 * Returns true when an event is fired during an active IME composition session.
 * Use this to skip custom key and change handlers so the browser can process
 * composition-related input natively.
 *
 * IME (Input Method Editor) users composing text in languages like Japanese,
 * Chinese, and Korean use keys such as Escape, Enter, and ArrowUp/Down as part
 * of the composition flow. Intercepting these keys unconditionally will
 * interfere — e.g., pressing Enter to confirm a kanji selection accidentally
 * submits a form, or pressing Escape to dismiss an IME overlay accidentally
 * closes a dialog.
 *
 * Accepts both React synthetic events (onKeyDown, onChange handlers) and native
 * KeyboardEvents (window.addEventListener handlers).
 *
 * ### Safari IME note
 * In Safari, keyboard events during an IME session may have `isComposing: false`
 * even when composition is active (a known WebKit bug). As a fallback, this
 * function also checks for `key === 'Process'` (the standard IME key value) and
 * `keyCode === 229` (the legacy equivalent reliably surfaced by Safari) on
 * `KeyboardEvent`s only.
 *
 * @example
 * function onKeyDown(e: React.KeyboardEvent) {
 *   if (isComposingEvent(e)) return;
 *   // ... custom handling
 * }
 *
 * @example
 * function onChange(e: React.ChangeEvent<HTMLInputElement>) {
 *   if (isComposingEvent(e)) return;
 *   // ... custom handling
 * }
 *
 * @example
 * window.addEventListener('keydown', (e) => {
 *   if (isComposingEvent(e)) return;
 *   // ... custom handling
 * });
 */
export function isComposingEvent(
  e: React.SyntheticEvent | KeyboardEvent
): boolean {
  const native =
    'nativeEvent' in e ? (e.nativeEvent as KeyboardEvent | InputEvent) : e;

  if (native?.isComposing) {
    return true;
  }

  // Safari IME fallback: `isComposing` is not reliably set in some Safari versions
  // during active IME composition. `key === 'Process'` is the standard signal;
  // `keyCode === 229` is the legacy equivalent that Safari surfaces. Both values
  // are only emitted by the browser when the IME has intercepted the key event.
  if (native instanceof KeyboardEvent) {
    return native.key === 'Process' || native.keyCode === 229;
  }

  return false;
}

const DEFAULT_IME_KEYS = ['ArrowUp', 'ArrowDown', 'Enter', 'Escape'];

/**
 * Returns true when a keydown event is part of an active IME composition session,
 * and stops propagation for IME-critical keys (`ArrowUp`, `ArrowDown`, `Enter`, `Escape`)
 * so that parent components (dialogs, comboboxes, etc.) do not react to keys the
 * IME is actively using.
 *
 * Use this in `onKeyDown` handlers for inputs and other focusable fields. For
 * container handlers (e.g. `Dialog`, `Sheet`) that only react to specific keys and
 * manage their own stopPropagation, use `isComposingEvent` directly instead.
 *
 * @example
 * function onKeyDown(e: React.KeyboardEvent) {
 *   if (isComposingKeyDown(e)) return;
 *   // ... custom handling
 * }
 */
export function isComposingKeyDown(
  e: React.KeyboardEvent | KeyboardEvent,
  stopPropagationKeys: readonly string[] = DEFAULT_IME_KEYS
): boolean {
  if (!isComposingEvent(e)) return false;
  if (stopPropagationKeys.includes(e.key)) {
    e.stopPropagation();
  }
  return true;
}
